import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { Search as SearchIcon } from '@material-ui/icons';
import { Tooltip } from '@material-ui/core';
import { StatusPill, SecondaryButton } from '../../shared/ui';
import { lightTheme as theme } from '../../shared/theme';
import { ClientQuotationsService } from './service';
import ServiceStatusView from './views/ServiceStatusView';
import {
  ActionsCell,
  ProductChip,
  ProductChipsContainer,
  ProductOverflowChip,
  TooltipProductsList,
} from './styles';

const VIEW_MODE = Object.freeze({
  KANBAN: 'kanban',
  LIST: 'list',
});

function createInitialColumnStates() {
  return {
    atencao: { expanded: true, showAll: false },
    andamento: { expanded: true, showAll: false },
    concluidos: { expanded: true, showAll: false },
  };
}

function ClientQuotationsPage({ currentLanguage, history }) {
  const clientLang = currentLanguage?.bepiService?.client || {};
  const sharedLang = currentLanguage?.bepiService?.shared || {};

  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState(VIEW_MODE.KANBAN);
  const [selectedProduct, setSelectedProduct] = useState('all');
  const [columnStates, setColumnStates] = useState(() => createInitialColumnStates());
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchQuotes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const nextQuotes = await ClientQuotationsService.getQuotations();
      if (!isMountedRef.current) return;
      setQuotes(nextQuotes);
    } catch (_requestError) {
      if (!isMountedRef.current) return;
      setError(clientLang.client_quotations_error_loading);
    } finally {
      if (!isMountedRef.current) return;
      setLoading(false);
    }
  }, [clientLang.client_quotations_error_loading]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const handleNewQuote = useCallback(() => {
    history.push('/bepi/client/quote');
  }, [history]);

  const handleOpenDetails = useCallback((quote) => {
    history.push(`/bepi/client/quote/details/${quote.id}`);
  }, [history]);

  const handleChangeViewMode = useCallback((nextViewMode) => {
    setViewMode(nextViewMode);
  }, []);

  const handleChangeProduct = useCallback((event) => {
    setSelectedProduct(event.target.value);
  }, []);

  const handleToggleColumnExpand = useCallback((columnKey) => {
    setColumnStates((prevState) => ({
      ...prevState,
      [columnKey]: {
        ...prevState[columnKey],
        expanded: !prevState[columnKey].expanded,
      },
    }));
  }, []);

  const handleToggleShowAll = useCallback((columnKey) => {
    setColumnStates((prevState) => ({
      ...prevState,
      [columnKey]: {
        ...prevState[columnKey],
        showAll: !prevState[columnKey].showAll,
      },
    }));
  }, []);

  const getStatusColorVariant = useCallback((status) => {
    return ClientQuotationsService.getStatusVariant(status);
  }, []);

  const productFilterItems = useMemo(() => {
    const uniqueProductsMap = new Map();

    quotes.forEach((quote) => {
      (quote.productNames || []).forEach((productName) => {
        const normalized = ClientQuotationsService.normalizeText(productName);
        if (!normalized) return;
        if (!uniqueProductsMap.has(normalized)) uniqueProductsMap.set(normalized, productName);
      });
    });

    const productItems = Array.from(uniqueProductsMap.values())
      .sort((a, b) => a.localeCompare(b, 'pt-BR'))
      .map((name) => ({ value: name, label: name }));

    return [
      {
        value: 'all',
        label: clientLang.client_quotations_filter_all_products,
      },
      ...productItems,
    ];
  }, [clientLang.client_quotations_filter_all_products, quotes]);

  const filteredQuotes = useMemo(() => {
    if (selectedProduct === 'all') return quotes;

    const selectedNormalized = ClientQuotationsService.normalizeText(selectedProduct);
    return quotes.filter((quote) => (quote.normalizedProductNames || []).includes(selectedNormalized));
  }, [quotes, selectedProduct]);

  const kanbanGroups = useMemo(() => {
    const groupedData = ClientQuotationsService.groupQuotesByStatus(filteredQuotes);

    return {
      atencao: {
        title: clientLang.client_quotations_pending_section,
        color: theme.colors.danger,
        items: groupedData.atencao,
        showAll: columnStates.atencao.showAll,
        order: 1,
      },
      andamento: {
        title: clientLang.client_quotations_processing_section,
        color: theme.colors.info,
        items: groupedData.andamento,
        showAll: columnStates.andamento.showAll,
        order: 2,
      },
      concluidos: {
        title: clientLang.client_quotations_finished_section,
        color: theme.colors.success,
        items: groupedData.concluidos,
        showAll: columnStates.concluidos.showAll,
        order: 3,
      },
    };
  }, [
    clientLang.client_quotations_finished_section,
    clientLang.client_quotations_pending_section,
    clientLang.client_quotations_processing_section,
    columnStates.atencao.showAll,
    columnStates.andamento.showAll,
    columnStates.concluidos.showAll,
    filteredQuotes,
  ]);

  const listRows = useMemo(() => {
    return filteredQuotes.map((quote) => ({
      id: quote.id,
      tipo: quote.tipo ? quote.tipo.charAt(0).toUpperCase() + quote.tipo.slice(1) : '-',
      ref: `#${quote.refId || quote.id}`,
      data: ClientQuotationsService.formatarData(quote.createdAt),
      dataSort: new Date(quote.createdAt).getTime(),
      valor: ClientQuotationsService.formatarValor(quote.valor, quote.status),
      status: quote.status || '-',
      local: quote.maintenanceExecutionLabel || '-',
      productNames: quote.productNames || [],
      rawData: quote,
    }));
  }, [filteredQuotes]);

  const renderProductColumn = useCallback((productNames) => {
    if (!Array.isArray(productNames) || productNames.length === 0) return <span>-</span>;

    const maxItems = 2;
    const visibleItems = productNames.slice(0, maxItems);
    const remainingCount = productNames.length - maxItems;

    return (
      <ProductChipsContainer>
        {visibleItems.map((name) => (
          <ProductChip key={name} label={name} size='small' />
        ))}

        {remainingCount > 0 ? (
          <Tooltip
            title={(
              <TooltipProductsList>
                {productNames.map((name) => (
                  <span key={name}>- {name}</span>
                ))}
              </TooltipProductsList>
            )}
            arrow
            placement='top'
          >
            <ProductOverflowChip label={`+${remainingCount}`} size='small' />
          </Tooltip>
        ) : null}
      </ProductChipsContainer>
    );
  }, []);

  const listColumns = useMemo(() => {
    return [
      {
        key: 'tipo',
        label: sharedLang.shared_clients_table_type,
        sortable: true,
        width: '10%',
      },
      {
        key: 'ref',
        label: clientLang.client_quotations_table_ref,
        sortable: true,
        width: '10%',
      },
      {
        key: 'data',
        label: sharedLang.shared_resp_client_date,
        sortable: true,
        sortValue: (row) => row.dataSort,
        width: '12%',
      },
      {
        key: 'valor',
        label: clientLang.client_quotations_table_value,
        sortable: true,
        width: '15%',
      },
      {
        key: 'status',
        label: sharedLang.shared_quotation_table_status,
        sortable: true,
        width: '15%',
        render: (row) => (
          <StatusPill variant={getStatusColorVariant(row.status)}>
            {row.status}
          </StatusPill>
        ),
      },
      {
        key: 'local',
        label: clientLang.client_quotations_table_local,
        sortable: true,
        width: '15%',
      },
      {
        key: 'productNames',
        label: clientLang.client_quotations_table_product,
        width: '15%',
        render: (row) => renderProductColumn(row.productNames),
      },
      {
        key: 'actions',
        label: '',
        align: 'right',
        width: '8%',
        render: (row) => (
          <ActionsCell>
            <Tooltip
              title={clientLang.client_quotations_view_details_btn}
              arrow
              placement='top'
            >
              <SecondaryButton
                onClick={() => handleOpenDetails(row.rawData)}
                size='sm'
              >
                <SearchIcon />
              </SecondaryButton>
            </Tooltip>
          </ActionsCell>
        ),
      },
    ];
  }, [
    clientLang.client_quotations_table_local,
    clientLang.client_quotations_table_product,
    clientLang.client_quotations_table_ref,
    clientLang.client_quotations_table_value,
    clientLang.client_quotations_view_details_btn,
    getStatusColorVariant,
    handleOpenDetails,
    renderProductColumn,
    sharedLang.shared_clients_table_type,
    sharedLang.shared_quotation_table_status,
    sharedLang.shared_resp_client_date,
  ]);

  return (
    <ServiceStatusView
      loading={loading}
      error={error}
      viewMode={viewMode}
      onChangeViewMode={handleChangeViewMode}
      selectedProduct={selectedProduct}
      onChangeProduct={handleChangeProduct}
      productFilterItems={productFilterItems}
      kanbanGroups={kanbanGroups}
      columnStates={columnStates}
      onToggleExpand={handleToggleColumnExpand}
      onToggleShowAll={handleToggleShowAll}
      listColumns={listColumns}
      listRows={listRows}
      onRefresh={fetchQuotes}
      onNewQuote={handleNewQuote}
      onOpenDetails={handleOpenDetails}
      resolveStatusVariant={getStatusColorVariant}
      currentLanguage={currentLanguage}
    />
  );
}

export default withRouter(ClientQuotationsPage);
